import { useCallback, useEffect, useMemo } from "react";
import { gridLayoutToChartLayout } from "../VNInvestMultiCharts";
import { allowsCloudWorkspace, resolveWorkspaceStore, } from "../workspace/WorkspaceStore";
export function useMultiWorkspacePersistence(deps) {
    const { layout, charts, currentWorkspaceName, activeChartId, storageAdapter, onWorkspaceSave, chartHandles, workspaceScope: scopeInput, } = deps;
    // W0.7: mọi lần đọc/ghi đi qua store, không gọi WorkspaceManager trực tiếp nữa.
    // Đổi local -> cloud khi EP-8 tới = sửa `resolveWorkspaceStore`, không sửa chỗ này.
    const scopeAllowsCloud = allowsCloudWorkspace(scopeInput?.tier ?? null, scopeInput?.licenseNarrowing);
    const store = useMemo(() => resolveWorkspaceStore({
        allowsCloud: scopeAllowsCloud,
        // Không có capability + không có backend ⇒ local, KHÔNG ném 501 (xem
        // `resolveWorkspaceStore`: khách enterprise mất sạch tính năng lưu nếu throw).
        backendReady: scopeInput?.backendReady,
        storageAdapter: storageAdapter ?? null,
        forceRemote: scopeInput?.forceRemote,
    }), [scopeAllowsCloud, storageAdapter, scopeInput?.backendReady, scopeInput?.forceRemote]);
    const saveWorkspace = useCallback(async (wsId) => {
        // Collect drawings from all charts
        const allDrawings = [];
        const allSyncGroups = [];
        chartHandles.current.forEach((handle, chartId) => {
            const drawings = handle.getDrawings?.() || [];
            allDrawings.push(...drawings);
            const groupId = handle.getSymbolLinkGroup?.();
            if (groupId) {
                const existingGroup = allSyncGroups.find(g => g.groupId === groupId);
                if (existingGroup) {
                    if (!existingGroup.chartIds.includes(chartId)) {
                        existingGroup.chartIds.push(chartId);
                    }
                }
                else {
                    const chartConfig = charts.find(c => c.id === chartId);
                    if (chartConfig) {
                        allSyncGroups.push({
                            groupId,
                            chartIds: [chartId],
                            symbol: chartConfig.symbol,
                        });
                    }
                }
            }
        });
        const gridLayout = {
            rows: layout.rows, cols: layout.cols,
            slots: (layout.slots ?? charts.map((c, i) => ({ id: c.id, rowStart: Math.floor(i / layout.cols) + 1, rowEnd: Math.floor(i / layout.cols) + 1, colStart: (i % layout.cols) + 1, colEnd: (i % layout.cols) + 1, symbol: c.symbol, timeframe: c.timeframe, symbolLinkGroup: c.symbolLinkGroup }))).map((s) => ({ ...s, symbolLinkGroup: s.symbolLinkGroup })),
            rowSizes: layout.rowSizes ?? Array(layout.rows).fill('1fr'), colSizes: layout.colSizes ?? Array(layout.cols).fill('1fr'),
            templateAreas: layout.templateAreas,
            areas: layout.areas ? Object.fromEntries(Object.entries(layout.areas).map(([key, value]) => [
                key,
                {
                    ...value,
                    id: value.chartId,
                    rowStart: 1, rowEnd: 1, colStart: 1, colEnd: 1
                }
            ])) : undefined,
        };
        const workspaceState = {
            id: wsId,
            name: currentWorkspaceName,
            layout: gridLayout,
            charts: charts.map((c) => ({
                id: c.id,
                symbol: c.symbol,
                timeframe: c.timeframe,
                visibleRange: c.visibleRange,
                symbolLinkGroup: c.symbolLinkGroup,
                engineState: c.engineState,
            })),
            drawings: allDrawings,
            syncGroups: allSyncGroups,
            activeChartId: activeChartId,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            version: 3,
        };
        // W0.7: `storageAdapter` đã được đưa vào store lúc tạo (useMemo ở trên),
        // nên không cần dựng adapter ở đây nữa.
        await store.save(workspaceState);
        onWorkspaceSave?.(wsId);
    }, [layout, charts, currentWorkspaceName, store, onWorkspaceSave, chartHandles, activeChartId]);
    const loadWorkspace = useCallback(async (wsId) => {
        const workspaceState = await store.load(wsId);
        if (!workspaceState)
            return;
        // Restore full GridLayout (backward compat: v1/v2 only had { rows, cols })
        const wl = workspaceState.layout;
        const chartLayout = (wl.templateAreas || wl.slots)
            ? gridLayoutToChartLayout(wl)
            : { rows: wl.rows, cols: wl.cols };
        deps.setLayoutState(chartLayout);
        deps.setChartsState(workspaceState.charts.map(c => ({
            id: c.id,
            symbol: c.symbol,
            timeframe: c.timeframe,
            visibleRange: c.visibleRange ? {
                timeRange: { from: c.visibleRange.timeRange.from, to: c.visibleRange.timeRange.to },
                priceRange: { min: 0, max: 0 },
                timeframe: c.timeframe,
            } : undefined,
            symbolLinkGroup: c.symbolLinkGroup,
            engineState: c.engineState,
        })));
        // Restore drawings to charts
        deps.chartHandles.current.forEach((handle, chartId) => {
            const chartDrawings = workspaceState.drawings.filter(d => {
                const chartConfig = workspaceState.charts.find(c => c.id === chartId);
                return chartConfig && d.symbol === chartConfig.symbol && d.timeframe === chartConfig.timeframe;
            });
            if (chartDrawings.length > 0) {
                handle.importDrawings?.(chartDrawings);
            }
        });
        // Restore sync groups
        workspaceState.syncGroups.forEach(group => {
            group.chartIds.forEach(chartId => {
                const handle = deps.chartHandles.current.get(chartId);
                if (handle) {
                    handle.setSymbolLinkGroup?.(group.groupId);
                }
            });
        });
        // Restore active chart
        if (workspaceState.activeChartId && deps.charts.some(c => c.id === workspaceState.activeChartId)) {
            deps.setActiveChartId(workspaceState.activeChartId);
        }
        deps.setCurrentWorkspaceId(wsId);
        deps.setCurrentWorkspaceName(workspaceState.name);
        deps.onWorkspaceLoad?.(wsId);
    }, [deps.storageAdapter, deps.onWorkspaceLoad, deps.chartHandles, deps.charts, deps.setLayoutState, deps.setChartsState, deps.setCurrentWorkspaceId, deps.setCurrentWorkspaceName, deps.setActiveChartId]);
    useEffect(() => {
        if (!deps.currentWorkspaceId)
            return;
        const t = setTimeout(() => saveWorkspace(deps.currentWorkspaceId), 2000);
        return () => clearTimeout(t);
    }, [layout, charts, deps.currentWorkspaceId, saveWorkspace]);
    /**
     * Đọc index workspace đã lưu từ store.
     *
     * Trước đây component cha giữ state `useState([])` và **không bao giờ** đọc index từ
     * storage ⇒ sau khi reload danh sách Load trống, dù storage còn tới 50 entry
     * (`WorkspaceManager.ts:105`). Đây là lỗ "mất dữ liệu" theo cảm nhận người dùng dù
     * dữ liệu vẫn còn. Store được tạo bên trong hook (`:76`) nên phải lộ ra từ đây thay vì
     * tạo thêm store thứ hai ở component cha.
     */
    const listWorkspaces = useCallback(async () => {
        try {
            return await store.list();
        }
        catch {
            // Storage hỏng / bị chặn => coi như chưa có workspace, KHÔNG crash chart.
            return [];
        }
    }, [store]);
    return { saveWorkspace, loadWorkspace, listWorkspaces, workspaceScope: store.scope };
}
